import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import { Mail } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const NewsletterForm = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    name: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/contact/newsletter/subscribe`, formData);
      
      toast({
        title: t('newsletterForm.successTitle'),
        description: response.data.message,
      });

      // Reset form
      setFormData({
        email: '',
        name: ''
      });
    } catch (error) {
      // Handle error message properly
      let errorMsg = t('newsletterForm.error');
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMsg = detail;
        } else if (Array.isArray(detail)) {
          errorMsg = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        } else if (typeof detail === 'object') {
          errorMsg = detail.msg || JSON.stringify(detail);
        }
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      toast({
        title: t('common.error'),
        description: errorMsg,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* Newsletter Header */}
      <div className="bg-gradient-to-r from-[#be1f1a] to-[#a71b17] text-white p-8 rounded-t-lg">
        <div className="flex items-center justify-center mb-4">
          <Mail className="h-12 w-12" />
        </div>
        <h3 className="text-2xl font-serif text-center mb-2">
          {t('newsletterForm.title')}
        </h3>
        <p className="text-center text-white/90">
          {t('newsletterForm.subtitle')}
        </p>
      </div>

      {/* Newsletter Form */}
      <div className="bg-white p-8 rounded-b-lg shadow-lg border border-gray-200 border-t-0">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Input
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder={t('newsletterForm.emailPlaceholder')}
              className="w-full text-lg py-6"
            />
          </div>

          <div>
            <Input
              name="name"
              type="text"
              value={formData.name}
              onChange={handleChange}
              placeholder={t('newsletterForm.namePlaceholder')}
              className="w-full text-lg py-6"
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#be1f1a] hover:bg-[#a71b17] text-white text-lg py-6"
          >
            {loading ? t('newsletterForm.subscribing') : t('newsletterForm.subscribe')}
          </Button>

          <p className="text-xs text-gray-500 text-center">
            {t('newsletterForm.disclaimer')}
          </p>
        </form>

        {/* Trust Indicators */}
        <div className="mt-6 grid grid-cols-3 gap-4 text-center text-sm text-gray-600">
          <div>
            <div className="font-semibold text-[#2C2420]">5000+</div>
            <div className="text-xs">{t('newsletterForm.subscribers')}</div>
          </div>
          <div>
            <div className="font-semibold text-[#2C2420]">{t('newsletterForm.weekly')}</div>
            <div className="text-xs">{t('newsletterForm.frequency')}</div>
          </div>
          <div>
            <div className="font-semibold text-[#2C2420]">{t('newsletterForm.exclusive')}</div>
            <div className="text-xs">{t('newsletterForm.content')}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewsletterForm;
